import {
	getAudioPlatform,
	getSharedAudioContext,
	getSpeechDetectionOptions,
} from '@/common/functions/audioPlatform';

const measureRms = (analyser, buffer) => {
	analyser.getByteTimeDomainData(buffer);

	let sum = 0;
	for (let index = 0; index < buffer.length; index += 1) {
		const sample = (buffer[index] - 128) / 128;
		sum += sample * sample;
	}

	return Math.sqrt(sum / buffer.length);
};

const resolveThresholds = (noiseFloor, config) => {
	const floor = Math.max(noiseFloor, config.minThreshold * 0.5);

	return {
		start: Math.max(config.minThreshold, floor * config.speechStartMultiplier),
		continue: Math.max(
			config.minThreshold * 0.65,
			floor * config.speechContinueMultiplier,
		),
	};
};

const reportActivityChange = ({
	config,
	reportedActivityRef,
	speechStarted,
	speechStartedAt,
	isSpeech,
	lastSpeechAt,
	now,
}) => {
	if (!config.onActivityChange) {
		return;
	}

	const isUserActive =
		speechStarted
		&& (isSpeech || now - lastSpeechAt < config.activityHoldMs);

	if (isUserActive && !reportedActivityRef.current) {
		if (now - speechStartedAt < config.minActiveMs) {
			return;
		}
	}

	if (isUserActive === reportedActivityRef.current) {
		return;
	}

	reportedActivityRef.current = isUserActive;
	config.onActivityChange(isUserActive);
};

export const waitForSpeechEnd = async (stream, options = {}) => {
	const platform = getAudioPlatform();
	const config = {
		...getSpeechDetectionOptions(platform),
		...options,
	};

	if (!stream) {
		return { reason: 'no-stream', spoke: false };
	}

	const context = await getSharedAudioContext();
	if (!context) {
		return { reason: 'no-audio-context', spoke: false };
	}

	return new Promise((resolve) => {
		const analyser = context.createAnalyser();
		analyser.fftSize = platform.isMobile ? 1024 : 2048;
		analyser.smoothingTimeConstant = platform.isMobile ? 0.22 : 0.16;

		const source = context.createMediaStreamSource(stream);
		source.connect(analyser);

		const buffer = new Uint8Array(analyser.fftSize);
		const startedAt = performance.now();
		const calibrationDoneAt = startedAt + config.calibrationMs;
		let noiseFloor = config.minThreshold;
		let calibrationSamples = 0;
		let speechStarted = false;
		let speechStartedAt = 0;
		let lastSpeechAt = 0;
		let frameId = 0;
		let settled = false;
		const reportedActivityRef = { current: false };

		const finish = (result) => {
			if (settled) {
				return;
			}

			settled = true;
			window.cancelAnimationFrame(frameId);
			source.disconnect();
			resolve(result);
		};

		const tick = () => {
			if (settled) {
				return;
			}

			const now = performance.now();
			const rms = measureRms(analyser, buffer);
			const { start: startThreshold, continue: continueThreshold } =
				resolveThresholds(noiseFloor, config);
			const earlySpeechThreshold =
				startThreshold * (config.earlySpeechMultiplier ?? 2.4);

			if (now < calibrationDoneAt) {
				if (rms < startThreshold) {
					noiseFloor += rms;
					calibrationSamples += 1;
				} else if (rms >= earlySpeechThreshold) {
					speechStarted = true;
					speechStartedAt = now;
					lastSpeechAt = now;
					reportActivityChange({
						config,
						reportedActivityRef,
						speechStarted,
						speechStartedAt,
						isSpeech: true,
						lastSpeechAt,
						now,
					});
				}

				frameId = window.requestAnimationFrame(tick);
				return;
			}

			if (calibrationSamples > 0) {
				noiseFloor = noiseFloor / calibrationSamples;
				calibrationSamples = 0;
			} else if (!speechStarted && rms < startThreshold) {
				noiseFloor = noiseFloor * 0.92 + rms * 0.08;
			}

			const isSpeech = speechStarted
				? rms >= continueThreshold
				: rms >= startThreshold;

			if (isSpeech) {
				if (!speechStarted) {
					speechStarted = true;
					speechStartedAt = now;
				}
				lastSpeechAt = now;
			}

			reportActivityChange({
				config,
				reportedActivityRef,
				speechStarted,
				speechStartedAt,
				isSpeech,
				lastSpeechAt,
				now,
			});

			if (!isSpeech) {
				if (
					speechStarted
					&& now - speechStartedAt >= config.minSpeechMs
					&& now - lastSpeechAt >= config.silenceMs
				) {
					if (reportedActivityRef.current) {
						reportedActivityRef.current = false;
						config.onActivityChange?.(false);
					}
					finish({ reason: 'silence', spoke: true });
					return;
				}

				if (now - startedAt >= config.maxListenMs) {
					if (reportedActivityRef.current) {
						reportedActivityRef.current = false;
						config.onActivityChange?.(false);
					}
					finish({ reason: 'timeout', spoke: speechStarted });
					return;
				}
			}

			frameId = window.requestAnimationFrame(tick);
		};

		frameId = window.requestAnimationFrame(tick);
	});
};

export const cancelSpeechMonitor = (cancelRef) => {
	if (cancelRef?.current) {
		cancelRef.current();
		cancelRef.current = null;
	}
};

export const bindCancellableSpeechMonitor = (stream, options = {}) => {
	let cancel = () => {};

	const promise = new Promise((resolve) => {
		let settled = false;

		const finish = (result) => {
			if (settled) {
				return;
			}
			settled = true;
			resolve(result);
		};

		cancel = () => {
			options.onActivityChange?.(false);
			finish({ reason: 'cancelled', spoke: false });
		};

		void waitForSpeechEnd(stream, options).then((result) => {
			options.onActivityChange?.(false);
			finish(result);
		});
	});

	return { promise, cancel };
};
